import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../../lib/constants';
import { useNotification } from '../../../../contexts/NotificationContext';
import { getCookie } from '../../../../lib/utils';

const useUserRoles = () => {
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const fetchRolesFromDB = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/get_roles`, { 
          withCredentials: true,
          headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }
        });
        
        const data = res.data.data || res.data;
        if (Array.isArray(data) && data.length > 0) {
          const dbRoleNames = data.map(r => {
            if (typeof r === 'string') return r;
            return r.name || r.role_name;
          }).filter(Boolean);
          
          // Use database roles strictly
          setRoles(dbRoleNames);
          console.log('Roles synchronized with database:', dbRoleNames);
        }
      } catch (err) {
        console.error('Failed to sync roles with database:', err);
      }
    };

    fetchRolesFromDB();
  }, []);

  return roles;
};

/**
 * Unified User service hook — Add or Edit based on user_id presence.
 */
export const useUser = (user_id) => {
  const { showNotification } = useNotification();
  const isEdit = !!user_id;
  const roles = useUserRoles();
  const [loading, setLoading] = useState(isEdit);
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API_BASE_URL}/get_user/${user_id}`, { 
      withCredentials: true,
      headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }
    })
      .then(res => {
        const u = res.data.data || res.data;
        setExistingData({ username: u.username, fullname: u.fullname, email: u.email, password: '', confirmPassword: '', role: u.role });
      })
      .catch(() => showNotification('error', 'Failed to fetch user data.'))
      .finally(() => setLoading(false));
  }, [user_id, isEdit, showNotification]);

  const submit = async (payload) => {
    const config = {
      headers: { 
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_access_token') 
      },
      withCredentials: true
    };
    
    if (isEdit) {
      return axios.put(`${API_BASE_URL}/update_user/${user_id}`, payload, config);
    }
    return axios.post(`${API_BASE_URL}/add_user`, payload, config);
  };

  return { isEdit, roles, loading, existingData, submit };
};

/**
 * User List service hook — fetch all + delete.
 */
export const useUserList = () => {
  const { showNotification } = useNotification();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/get_users`, { 
      withCredentials: true,
      headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }
    })
      .then(res => { if (Array.isArray(res.data.data)) setUsers(res.data.data); else showNotification('error', 'Unexpected API response.'); })
      .catch(() => showNotification('error', 'Failed to fetch user data.'))
      .finally(() => setLoading(false));
  }, [showNotification]);

  const deleteUser = async (user) => {
    const res = await axios.delete(`${API_BASE_URL}/delete_user/${user.user_id}`, { 
      withCredentials: true,
      headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }
    });
    if (res.status === 200) setUsers(prev => prev.filter(u => u.user_id !== user.user_id));
  };

  return { users, loading, deleteUser };
};
